import { Locale, format, parseISO, isSameMonth, isSameYear } from "date-fns"
import I18n from "i18n-js"
/* eslint import/namespace: ['error', { allowComputed: true }] */
import * as locales from "date-fns/locale"

type Options = Parameters<typeof format>[2]

const getLocale = (): Locale => {
  const currentLocale = I18n.currentLocale().replace("-", "")

  return locales[currentLocale] || locales.enUS
}

export const datesFormats = {
  shortenedMonthName: "MMM",
  shortenedMonthNameWithYear: "MMM yyyy",
  monthWithYear: "MMMM yyyy",
  monthDayYear: "MMM dd, yyyy",
  shortenedMonthNameWithDay: "MMM d",
  shortenedDayMonth: "EEE, MMM dd",
  monthWithDay: "MMMM d",
}

export const formatLocalDate = (date: string, dateFormat?: string, options?: Options) => {
  try {
    const locale = getLocale()
    const dateOptions = {
      ...options,
      locale,
    }
    return format(parseISO(date), dateFormat ?? "MMM dd, yyyy", dateOptions)
  } catch {
    return ""
  }
}

const milisecondsInMinute = 60000

// There was a problem with the display of dates, that in the UTC timezone everything worked fine,
// but in the timezone that was before UTC the formatDate function showed the day before
// (due to the change of timezone). Using this function will get the same date everywhere - but
// I would not use it to show the time, due to only one time zone.
export const convertDateToUTCTimeZone = (date: Date) => {
  return new Date(date.getTime() + date.getTimezoneOffset() * milisecondsInMinute)
}

export const formatDate = (date: string, dateFormat?: string, options?: Options) => {
  try {
    const locale = getLocale()
    const dateOptions = {
      ...options,
      locale,
    }
    return format(
      convertDateToUTCTimeZone(parseISO(date)),
      dateFormat ?? "MMM dd, yyyy",
      dateOptions,
    )
  } catch {
    return ""
  }
}

/**
 * Format dates for booked cruises
 *
 * @param {string} startDate
 * @param {string} endDate
 * @param {string} dateFormat
 * @return {string}
 */
export function formatBookedCruiseDates(
  startDate: string,
  endDate: string,
  dateFormat: string = datesFormats.shortenedMonthNameWithDay,
): string {
  const start = new Date(startDate)
  const end = new Date(endDate)

  if (isSameMonth(start, end) && isSameYear(start, end)) {
    return `${formatLocalDate(startDate, dateFormat)} – ${formatLocalDate(endDate, "d")}`
  } else {
    return `${formatLocalDate(startDate, dateFormat)} – ${formatLocalDate(endDate, dateFormat)}`
  }
}

export const formatDateToString = (dateArg: Date | null, dateFormat: string): string | null => {
  if (!dateArg) return null
  return format(dateArg, dateFormat)
}

/**
 * Function to convert a date in "YYYY-MM-DD" format to Date object
 * @param {string} dateString
 * @return {Date}
 */
export function dateStringToDate(dateString): Date {
  const dateParts = dateString.split("-")
  const year = parseInt(dateParts[0])
  const month = parseInt(dateParts[1]) - 1 // Months are zero-based in JavaScript
  const day = parseInt(dateParts[2])

  return new Date(year, month, day)
}

// TODO we might be able to make this locale aware ie in the US we use M/d/yyyy and in Europe we use d/M/yyyy
// Convert a date in "YYYY-MM-DD" format to "M/d/yyyy" format
export const formatCruiseFilterDate = (startDate: string | null) => {
  if (!startDate) return ""
  const date = new Date(startDate)
  return formatDate(date.toISOString(), "M/d/yyyy")
}

/**
 * Convert a date in "MM/DD/YYYY" format to "YYYY-MM-DD" format
 * @param {string} date
 * @return {string}
 */
export const convertMonthDayYearToDateString = (date: string): string => {
  if (!date) return ""
  if (date.includes("/")) {
    const dateParts = date.split("/")
    const month = dateParts[0]
    const day = dateParts[1]
    const year = dateParts[2]
    return `${year}-${month}-${day}`
  }
  return date
}
